/**
 * Data Dictionary Utilities
 * Handles CSV file conversion and validation for Data Dictionary imports
 * 
 * CRITICAL: Data Dictionary API expects:
 *   - SEMICOLON-delimited CSV format: code;market;name;extra
 *   - Proper CSV quote escaping (double-quotes "" for literal quotes)
 *   - Example: 4;ALL;Latte Macchiato;"{""grams"": ""2""}"
 *   - CSV sent as-is WITHOUT any transformation or normalization
 */

/**
 * Convert a File object to base64 string
 * Used for uploading CSV files to the Data Dictionary API
 * 
 * The CSV is sent as-is without normalization. The backend expects
 * the exact format from the CSV file with proper quote escaping intact.
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const content = reader.result as string;
        
        console.log('[fileToBase64] Reading CSV file:', {
          fileName: file.name,
          fileSize: file.size,
          firstLine: content.split('\n')[0],
        });
        
        // Convert text to ArrayBuffer for proper binary encoding
        const encoder = new TextEncoder();
        const arrayBuffer = encoder.encode(content).buffer;
        const bytes = new Uint8Array(arrayBuffer);
        
        // Convert bytes to binary string
        let binaryString = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binaryString += String.fromCharCode(bytes[i]);
        }
        
        // Convert binary string to base64
        const base64 = btoa(binaryString);
        
        if (!base64) {
          reject(new Error('Base64 string is empty'));
          return;
        }
        
        console.log('[fileToBase64] Successfully converted to base64:', {
          contentLength: content.length,
          base64Length: base64.length,
        });
        
        resolve(base64);
      } catch (error) {
        console.error('[fileToBase64] Error:', error);
        reject(new Error(`Failed to process file data: ${error instanceof Error ? error.message : String(error)}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    // Read file as text
    reader.readAsText(file);
  });
}

/**
 * Parse CSV file content into rows and columns
 */
export async function parseCSV(file: File): Promise<{ headers: string[]; rows: string[][] } | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const content = reader.result as string;
        const lines = content.trim().split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          resolve(null);
          return;
        }

        // Detect delimiter from header line
        const delimiter = detectDelimiter(lines[0]);
        console.log('Detected CSV delimiter:', delimiter);

        // Parse CSV - simple implementation that handles basic CSV format
        const headers = parseCSVLine(lines[0], delimiter);
        const rows = lines.slice(1)
          .map(line => parseCSVLine(line, delimiter))
          .filter(row => row.some(cell => cell)); // Filter out completely empty rows

        resolve({ headers, rows });
      } catch (error) {
        reject(new Error('Failed to parse CSV file'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}

/**
 * Parse a single CSV line handling quoted values with commas or semicolons
 * Detects delimiter (comma or semicolon) based on first line
 */
function parseCSVLine(line: string, delimiter = ','): string[] {
  const result = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // Field separator
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Detect CSV delimiter (comma or semicolon)
 */
function detectDelimiter(headerLine: string): string {
  const commaCount = (headerLine.match(/,/g) || []).length;
  const semicolonCount = (headerLine.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
}

/**
 * Validate CSV file before upload
 */
export function validateCsvFile(
  file: File,
  maxSizeMB = 10,
): { valid: boolean; error?: string } {
  if (!file.name.endsWith('.csv')) {
    return { valid: false, error: 'Please select a CSV file' };
  }

  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `File is too large (max ${maxSizeMB}MB)`,
    };
  }

  return { valid: true };
}

/**
 * Validate CSV format for different dictionary types
 * 
 * Expected formats:
 * - Recipes: code;market;name;extra (4 columns)
 * - Products: code;market;name;extra;dict_recipe_code (5 columns)
 * - Events: code;name;type;extra (4 columns)
 * - Subevents: code;name;event_code (3 columns)
 * 
 * Checks for:
 * 1. Correct number of columns
 * 2. No invalid starting quotes on data rows
 * 3. Header row matches expected format
 */
export function validateDataDictionaryCSVFormat(
  content: string,
  dictionaryType: 'recipes' | 'products' | 'events' | 'subevents' = 'recipes',
): { valid: boolean; error?: string } {
  // Define expected formats for each dictionary type
  const formats: Record<string, { header: string; columnCount: number; description: string }> = {
    recipes: {
      header: 'code;market;name;extra',
      columnCount: 4,
      description: 'Recipes (4 columns)',
    },
    products: {
      header: 'code;market;name;extra;dict_recipe_code',
      columnCount: 5,
      description: 'Products (5 columns)',
    },
    events: {
      header: 'code;name;type;extra',
      columnCount: 4,
      description: 'Events (4 columns)',
    },
    subevents: {
      header: 'code;name;event_code',
      columnCount: 3,
      description: 'Subevents (3 columns)',
    },
  };

  const format = formats[dictionaryType] || formats.recipes;

  try {
    const lines = content.trim().split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      return { valid: false, error: 'CSV file must contain at least a header and one data row' };
    }

    // Check header - trim it first to allow minor whitespace issues
    const headerLine = lines[0].trim();
    const expectedHeader = format.header;
    
    if (headerLine !== expectedHeader) {
      // Provide specific error messages based on what's wrong
      let errorDetails = '';
      
      // Check if header has extra quotes
      if (headerLine.includes('"')) {
        errorDetails = '\n\n⚠️  The header line contains quote characters ("). Headers should not have quotes.\n\n' +
          'Example of INCORRECT format:\n' +
          `"${expectedHeader}"\n\n` +
          'Example of CORRECT format:\n' +
          `${expectedHeader}`;
      }
      // Check if it has wrong delimiters
      else if (headerLine.includes(',')) {
        errorDetails = '\n\n⚠️  The file appears to use commas (,) as delimiters instead of semicolons (;).\n\n' +
          'CSV files must use semicolons (;) to separate columns:\n' +
          `${expectedHeader}`;
      }
      // Check for column count mismatch
      else {
        const semicolonCount = (headerLine.match(/;/g) || []).length;
        const actualColumns = semicolonCount + 1;
        if (actualColumns !== format.columnCount) {
          errorDetails = `\n\n⚠️  The header has ${actualColumns} column(s) instead of ${format.columnCount}.\n\n` +
            `Expected format for ${format.description}:\n` +
            `${expectedHeader}`;
        } else {
          errorDetails = '\n\n⚠️  The header does not match the expected format.\n\n' +
            `Expected:\n` +
            `${expectedHeader}`;
        }
      }
      
      return {
        valid: false,
        error: `Invalid header format.\n\n` +
          `Expected: ${expectedHeader}\n\n` +
          `Got: ${headerLine}${errorDetails}`,
      };
    }

    // Validate each data row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip completely empty lines
      if (!line) {
        continue;
      }

      // Check Case 1: No quotes at the beginning of the line (data should not start with ")
      if (line.startsWith('"') && !line.startsWith('""')) {
        return {
          valid: false,
          error: `❌ Row ${i + 1} has INVALID format\n\n` +
            `⚠️  Extra quotes at the beginning of the line detected.\n\n` +
            `Your data rows are wrapped with extra quote characters that should not be there.\n\n` +
            `❌ INCORRECT - This is what you have:\n` +
            `"0;ALL;Product Name;{};0"\n` +
            `   ↑ Remove this quote                           ↑ And this quote\n\n` +
            `✓ CORRECT - This is what you need:\n` +
            `0;ALL;Product Name;{};0\n\n` +
            `The issue: Each data row starts and ends with a double quote (") that needs to be removed.\n` +
            `This often happens when the CSV was processed or exported incorrectly.\n\n` +
            `Solution: Remove the leading and trailing quote characters from all data rows.`,
        };
      }

      // Count semicolons - need exactly (columnCount - 1) to have correct number of columns
      const semicolonCount = (line.match(/;/g) || []).length;
      const expectedSemicolons = format.columnCount - 1;
      
      if (semicolonCount !== expectedSemicolons) {
        const columnCount = semicolonCount + 1;
        return {
          valid: false,
          error: `❌ Row ${i + 1} has ${columnCount} column(s) instead of ${format.columnCount}.\n\n` +
            `⚠️  Expected ${format.columnCount} columns separated by ${expectedSemicolons} semicolons:\n` +
            `${expectedHeader}\n\n` +
            `Your row has ${semicolonCount} semicolon(s).\n\n` +
            `Row content:\n"${line}"\n\n` +
            `Please check if you're missing or have extra semicolons in this row.`,
        };
      }

      // Parse the line to check structure
      const parts = line.split(';');
      
      // Check each part (first 3 columns are always required)
      if (!parts[0] || !parts[0].trim()) {
        return { 
          valid: false, 
          error: `❌ Row ${i + 1}: The 'code' column (first column) is empty.\n\n` +
            `All rows must have: ${expectedHeader}` 
        };
      }
      
      if (!parts[1] || !parts[1].trim()) {
        return { 
          valid: false, 
          error: `❌ Row ${i + 1}: The 'market' column (second column) is empty.\n\n` +
            `All rows must have: ${expectedHeader}` 
        };
      }
      
      if (!parts[2] || !parts[2].trim()) {
        return { 
          valid: false, 
          error: `❌ Row ${i + 1}: The 'name' column (third column) is empty.\n\n` +
            `All rows must have: ${expectedHeader}` 
        };
      }
      
      // Note: 'extra' and other columns can be empty
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: `Error validating CSV format: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Validate recipe CSV format (legacy - use validateDataDictionaryCSVFormat instead)
 */
export function validateRecipeCSVFormat(content: string): { valid: boolean; error?: string } {
  return validateDataDictionaryCSVFormat(content, 'recipes');
}

/**
 * Download file from S3 presigned URL
 * Uses window.location for direct download to avoid CORS issues
 */
export async function downloadFromPresignedUrl(
  presignedUrl: string,
  filename: string,
): Promise<void> {
  try {
    // Create an anchor element and trigger download
    const link = document.createElement('a');
    link.href = presignedUrl;
    link.download = filename;
    link.setAttribute('target', '_blank');
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw new Error(
      `Failed to download file: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
